import type { ReactNode } from 'react'

/**
 * The English for the polling code lists.
 *
 * **The server owns the code list; this file owns the English.** That division is
 * the whole reason this file is safe to hand-edit: adding a label here can never
 * put an option in a picker, and no picker can ever offer a code the server did
 * not serve. Every option list comes from `useVocabulary.ts` →
 * `GET /surveys/vocabulary`; a hardcoded array of codes in this file — or in any
 * screen — is the PollSphere defect this port exists to fix, where the filters
 * offered `STATION`, `TRACKING`, `OPPOSITION_RESEARCH`, `YOUTH`, `WOMEN` and
 * `RURAL`, matched nothing, and returned an empty list with no error.
 *
 * Every lookup falls back to the RAW CODE. Not to "Unknown": two codes this build
 * has never heard of must not render as the same thing, and a code showing up
 * verbatim on screen is the forward-compatibility mechanism working out loud —
 * somebody sees `MESSAGE_TEST` and knows exactly which label is missing. A
 * prettified fallback would hide that, and a shared "Unknown" would erase it.
 *
 * A null or empty code is the one case that is NOT a code: it renders as an em
 * dash, the same "no figure" mark the numeric formatters below use.
 */

/** One shape for every lookup: dash for absent, the label, or the code itself. */
const label = (map: Record<string, string>, c?: string | null) =>
  !c ? '—' : (map[c] ?? c)

// ── poll status ─────────────────────────────────────────────────────────────

const POLL_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft', IN_REVIEW: 'In review', APPROVED: 'Approved', LIVE: 'Live',
  PAUSED: 'Paused', CLOSED: 'Closed', ARCHIVED: 'Archived',
}
export const pollStatusLabel = (c?: string | null) => label(POLL_STATUS_LABEL, c)

/**
 * ONE accent per screen: status carries colour and everything else is ink.
 *
 * PollSphere's Intelligence tab colours nine things at once, which is the same as
 * colouring none of them.
 */
export const pollStatusTone = (c?: string | null): string => {
  switch (c) {
    case 'LIVE': return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    case 'PAUSED': return 'bg-amber-500/10 text-amber-600 border-amber-500/30'
    case 'IN_REVIEW': return 'bg-sky-500/10 text-sky-600 border-sky-500/30'
    case 'CLOSED': case 'ARCHIVED': return 'bg-muted text-muted-foreground border-border'
    default: return 'bg-muted/60 text-foreground/70 border-border'
  }
}

// ── how a poll is classified ────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
  BASELINE: 'Baseline', TRACKER: 'Tracker', ISSUE: 'Issue', CANDIDATE: 'Candidate',
  MESSAGE_TEST: 'Message test', EXIT_POLL: 'Exit poll', QUICK_COUNT: 'Quick count',
}
export const categoryLabel = (c?: string | null) => label(CATEGORY_LABEL, c)

const PURPOSE_LABEL: Record<string, string> = {
  BASELINE_MEASUREMENT: 'Baseline measurement', TREND_TRACKING: 'Trend tracking',
  ISSUE_DIAGNOSTICS: 'Issue diagnostics', OPPOSITION_INTEL: 'Opposition intelligence',
  CANDIDATE_VIABILITY: 'Candidate viability', GET_OUT_THE_VOTE: 'Get out the vote',
  MESSAGE_TESTING: 'Message testing', TURNOUT_MODELLING: 'Turnout modelling',
}
export const purposeLabel = (c?: string | null) => label(PURPOSE_LABEL, c)

const AUDIENCE_LABEL: Record<string, string> = {
  ALL_VOTERS: 'All voters', REGISTERED_VOTERS: 'Registered voters',
  UNDECIDED_VOTERS: 'Undecided voters', OPPOSITION_SUPPORTERS: 'Opposition supporters',
  PARTY_MEMBERS: 'Party members', YOUTH_VOTERS: 'Youth voters',
  WOMEN_VOTERS: 'Women voters', RURAL_VOTERS: 'Rural voters', URBAN_VOTERS: 'Urban voters',
}
export const audienceLabel = (c?: string | null) => label(AUDIENCE_LABEL, c)

const FREQUENCY_LABEL: Record<string, string> = {
  ONE_TIME: 'One-off', DAILY: 'Daily', WEEKLY: 'Weekly', FORTNIGHTLY: 'Fortnightly',
  MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', EVENT_DRIVEN: 'Event-driven',
  CONTINUOUS: 'Continuous',
}
export const frequencyLabel = (c?: string | null) => label(FREQUENCY_LABEL, c)

/** How the interview was conducted, not how it was submitted — see {@link channelLabel}. */
const METHODOLOGY_LABEL: Record<string, string> = {
  F2F: 'Face to face', CATI: 'Telephone (CATI)', ONLINE: 'Online', USSD: 'USSD',
  SMS: 'SMS', WHATSAPP: 'WhatsApp', HYBRID: 'Mixed mode',
}
export const methodologyLabel = (c?: string | null) => label(METHODOLOGY_LABEL, c)

// ── the instrument ──────────────────────────────────────────────────────────

const QUESTION_TYPE_LABEL: Record<string, string> = {
  SINGLE_CHOICE: 'Single choice', MULTI_CHOICE: 'Multiple choice', YES_NO: 'Yes / No',
  RATING_SCALE: 'Rating scale', LIKERT: 'Likert', NPS: 'NPS', RANKING: 'Ranking',
  NUMBER: 'Number', OPEN_TEXT: 'Open text', DATE: 'Date', MATRIX: 'Matrix',
  GEOLOCATION: 'Location', MEDIA_CAPTURE: 'Photo', CONSENT: 'Consent',
}
export const questionTypeLabel = (c?: string | null) => label(QUESTION_TYPE_LABEL, c)

// ── fieldwork ───────────────────────────────────────────────────────────────

const ASSIGNMENT_STATUS_LABEL: Record<string, string> = {
  ASSIGNED: 'Assigned', IN_PROGRESS: 'In progress', COMPLETED: 'Completed',
  EXPIRED: 'Expired', REVOKED: 'Revoked',
}
export const assignmentStatusLabel = (c?: string | null) => label(ASSIGNMENT_STATUS_LABEL, c)

/**
 * The outcome of one contact attempt — the AAPOR disposition.
 *
 * These are the denominators. `NON_CONTACT` and `REFUSAL` are what separate a
 * response rate from a cooperation rate, so they are named as the field
 * recognises them rather than as codes.
 */
const DISPOSITION_LABEL: Record<string, string> = {
  COMPLETE: 'Completed interview', PARTIAL: 'Partial interview', REFUSAL: 'Refused',
  NON_CONTACT: 'No contact', INELIGIBLE: 'Ineligible', LANGUAGE_BARRIER: 'Language barrier',
  CALLBACK: 'Callback arranged',
}
export const dispositionLabel = (c?: string | null) => label(DISPOSITION_LABEL, c)

/** How the interview REACHED the server. `CATI` appears here and in the methodology
 *  list because a telephone poll can still be keyed in from the web app afterwards. */
const CHANNEL_LABEL: Record<string, string> = {
  FIELD_APP: 'Field app', WEB: 'Web', CATI: 'Telephone (CATI)', SMS: 'SMS', USSD: 'USSD',
  WHATSAPP: 'WhatsApp', IVR: 'IVR', KIOSK: 'Kiosk', API: 'API',
}
export const channelLabel = (c?: string | null) => label(CHANNEL_LABEL, c)

// ── the sample design ───────────────────────────────────────────────────────

const SELECTION_LABEL: Record<string, string> = {
  PROPORTIONAL: 'Proportional', EQUAL: 'Equal', DISPROPORTIONATE: 'Disproportionate',
}
export const selectionLabel = (c?: string | null) => label(SELECTION_LABEL, c)

const QUOTA_SOURCE_LABEL: Record<string, string> = {
  RESPONDENT_FIELD: 'Respondent field', QUESTION: 'Answer to a question',
  GEOGRAPHY: 'Geography',
}
export const quotaSourceLabel = (c?: string | null) => label(QUOTA_SOURCE_LABEL, c)

/** A quota cell target is either a headcount or a share of the target sample. */
const QUOTA_BASIS_LABEL: Record<string, string> = { COUNT: 'Count', SHARE: 'Share' }
export const quotaBasisLabel = (c?: string | null) => label(QUOTA_BASIS_LABEL, c)

// ── the register's vocabulary ───────────────────────────────────────────────

const GENDER_LABEL: Record<string, string> = {
  MALE: 'Male', FEMALE: 'Female', OTHER: 'Other', UNDISCLOSED: 'Undisclosed',
}
export const genderLabel = (c?: string | null) => label(GENDER_LABEL, c)

/**
 * Age bands, with a real en dash.
 *
 * The `A` prefix is there because the codes are Java enum constants and cannot
 * start with a digit. Nobody outside the server needs to know that, but the raw
 * code is still the fallback: `A18_24` on screen is a missing label, and
 * inventing "18-24" from an unrecognised code would be guessing at a range.
 */
const AGE_BAND_LABEL: Record<string, string> = {
  A18_24: '18–24', A25_34: '25–34', A35_44: '35–44', A45_54: '45–54',
  A55_64: '55–64', A65_PLUS: '65+',
}
export const ageBandLabel = (c?: string | null) => label(AGE_BAND_LABEL, c)

// ── responses and integrity ─────────────────────────────────────────────────

const RESPONSE_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft', SUBMITTED: 'Submitted', COMPLETE: 'Complete', PARTIAL: 'Partial',
  FLAGGED: 'Flagged', REJECTED: 'Rejected', WITHDRAWN: 'Withdrawn',
}
export const responseStatusLabel = (c?: string | null) => label(RESPONSE_STATUS_LABEL, c)

const MEDIA_KIND_LABEL: Record<string, string> = {
  AUDIO_INTERVIEW: 'Interview recording', AUDIO_CONSENT: 'Consent recording',
  PHOTO: 'Photo', SIGNATURE: 'Signature',
}
export const mediaKindLabel = (c?: string | null) => label(MEDIA_KIND_LABEL, c)

/**
 * The verifier's finding on a back-check.
 *
 * `FABRICATED` is not a synonym for a mismatch: it flags every other interview by
 * that interviewer on that poll, so it is worded as the accusation it is.
 */
const BACK_CHECK_OUTCOME_LABEL: Record<string, string> = {
  VERIFIED: 'Verified', PARTIAL_MISMATCH: 'Partial mismatch', FABRICATED: 'Fabricated',
  UNREACHABLE: 'Respondent unreachable', NOT_CONDUCTED: 'Not conducted',
}
export const backCheckOutcomeLabel = (c?: string | null) => label(BACK_CHECK_OUTCOME_LABEL, c)

/**
 * The integrity rules, in words a supervisor can act on.
 *
 * Deliberately explanatory rather than terse: a queue row reading `GPS_CLUSTERED`
 * tells somebody nothing about what to do next.
 */
const FLAG_RULE_LABEL: Record<string, string> = {
  SPEEDER: 'Unusually fast',
  STRAIGHTLINER: 'Same answer throughout a battery',
  NON_DIFFERENTIATION: 'No variation at all across a battery',
  GPS_MISSING: 'No location fix',
  GPS_IMPRECISE: 'Vague location fix',
  GPS_OUT_OF_SCOPE: 'Recorded outside the assigned area',
  IMPOSSIBLE_TRAVEL: 'Impossible journey between interviews',
  GPS_CLUSTERED: 'Many interviews from one spot',
  DUPLICATE_PHONE: 'Same contact as an earlier interview',
  DUPLICATE_DEVICE: 'Unusually many interviews from one device',
  NEAR_DUPLICATE_ANSWERS: 'Nearly identical to another interview',
  OFF_HOURS: 'Submitted outside fieldwork hours',
  AUDIO_MISSING: 'Required recording is missing',
  AUDIO_TOO_SHORT: 'Recording covers only part of the interview',
  DURATION_DISAGREEMENT: 'Device and server disagree on the duration',
  BACK_CHECK_FAILED: 'Another interview by this person was fabricated',
}
export const flagRuleLabel = (c?: string | null) => label(FLAG_RULE_LABEL, c)

export const flagSeverityTone = (c?: string | null): string => {
  switch (c) {
    case 'HIGH': return 'bg-rose-500/10 text-rose-600 border-rose-500/30'
    case 'WARN': return 'bg-amber-500/10 text-amber-600 border-amber-500/30'
    default: return 'bg-muted text-muted-foreground border-border'
  }
}

// ── analysis and output ─────────────────────────────────────────────────────

const WEIGHT_METHOD_LABEL: Record<string, string> = {
  DESIGN_ONLY: 'Design weights only', POST_STRATIFICATION: 'Post-stratified', RAKING: 'Raked',
}
export const weightMethodLabel = (c?: string | null) => label(WEIGHT_METHOD_LABEL, c)

const EXPORT_FORMAT_LABEL: Record<string, string> = {
  CSV: 'Dataset (CSV)', VERBATIM_CSV: 'Verbatims (CSV)', CODEBOOK_CSV: 'Codebook (CSV)',
  SPSS_SAV: 'SPSS (.sav)', STATA_DTA: 'Stata (.dta)',
}
export const exportFormatLabel = (c?: string | null) => label(EXPORT_FORMAT_LABEL, c)

/**
 * The two formats the server declares and refuses.
 *
 * Not a code list a picker is built from — `ExportFormat` is not one of the ten
 * vocabularies `GET /surveys/vocabulary` serves, so there is nothing to fetch —
 * but a POLICY annotation on the codes that are. The picker offers both on
 * purpose: the server's refusal explains the trade and points at CSV + codebook,
 * and a format silently missing from a dropdown is one somebody assumes is coming.
 */
export const UNIMPLEMENTED_FORMATS = new Set(['SPSS_SAV', 'STATA_DTA'])

// ── numbers ─────────────────────────────────────────────────────────────────

const asNumber = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * A proportion as a percentage — and `null` renders as an em dash, never as 0%.
 *
 * This is the single most important formatter in the feature. "0%" and "we have
 * no figure" are different statements, and the server is careful to distinguish
 * them; a `?? 0` here would throw that away at the last step.
 */
export function pct(value: string | number | null | undefined, decimals = 1): string {
  const n = asNumber(value)
  if (n === null) return '—'
  return `${(n * 100).toFixed(decimals)}%`
}

/** Percentage points, for a margin of error: `±2.4pp`. */
export function pp(value: string | number | null | undefined, decimals = 1): string {
  const n = asNumber(value)
  if (n === null) return '—'
  return `±${(n * 100).toFixed(decimals)}pp`
}

export function num(value: string | number | null | undefined, decimals = 2): string {
  const n = asNumber(value)
  if (n === null) return '—'
  return n.toFixed(decimals)
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString()
}

/**
 * A count that may arrive as a decimal STRING — an effective n, for instance.
 *
 * <p>Separate from {@link count} so the null case cannot be lost to a
 * `Number(x ?? 0)` on the way in: `count(Number(null ?? 0))` renders "0", and an
 * effective n of zero is a claim that the weighting left no usable sample.</p>
 */
export function countish(value: string | number | null | undefined): string {
  const n = asNumber(value)
  if (n === null) return '—'
  return Math.round(n).toLocaleString()
}

/** `12m 04s`. Durations on this screen are minutes, never raw seconds. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${String(s).padStart(2, '0')}s`
}

/**
 * The base statement that belongs under every published figure.
 *
 * "Base: 1,204 who expressed a preference." A percentage without one is a
 * percentage nobody can weigh.
 */
export function baseStatement(baseN: number, baseCode: string): string {
  const of = baseCode === 'SUBSTANTIVE'
    ? 'who expressed a preference'
    : baseCode === 'ALL_ANSWERED' ? 'who answered' : 'interviewed'
  return `Base: ${baseN.toLocaleString()} ${of}.`
}

/** A small inline pill. One shape for every code in this feature. */
export function Pill({ tone, children }: { tone?: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-erp-xs ${
      tone ?? 'bg-muted/60 text-foreground/70 border-border'}`}>
      {children}
    </span>
  )
}

// ── what a choice IMPLIES ───────────────────────────────────────────────────
//
// The configurator asks somebody to pick a category, a purpose, an audience, a
// method and a frequency before they have written a single question, and until
// now every one of those was a bare code in a `<select>`. "I chose CANDIDATE
// and I do not know what that means for me" is a fair complaint about a form
// that offers a consequential choice and explains none of it.
//
// **These sentences are English for a code, exactly like the label maps above —
// they are NOT a second source of truth for the option list.** Every picker
// still renders `useCodes(...)`, and a code with no sentence here renders with
// no sentence rather than being dropped. That asymmetry is the whole safety
// property: adding prose can never add an option, and removing prose can never
// remove one.

/** Falls back to empty, never to filler: a made-up implication is worse than none. */
const implication = (map: Record<string, string>, c?: string | null) => (c ? map[c] ?? '' : '')

const CATEGORY_IMPLIES: Record<string, string> = {
  BASELINE: 'A one-off reading before anything has been tried. Nothing compares to it yet — '
    + 'it is what later waves will be compared against.',
  TRACKER: 'A repeated reading. Put it in a tracker and give it a wave, or the trend screen '
    + 'has nothing to line it up with.',
  ISSUE: 'A deep dive into one subject. Usually many questions, one topic, and no ballot.',
  CANDIDATE: 'About a named person or a field of them. Link the candidates on the '
    + 'cross-module step so their drawers and the crosstabs can find this poll.',
  MESSAGE_TEST: 'Two or more framings of the same thing, asked of different people. Randomise '
    + 'the option order so the framing is what varies and not the reading order.',
  EXIT_POLL: 'Asked at a station on polling day. Expect a short instrument, a tight window and '
    + 'GPS that matters — every interview should fall inside the station it claims.',
  QUICK_COUNT: 'A parallel tabulation, not an opinion survey. The numbers are counts of a '
    + 'result, and weighting them to a population would be wrong.',
}
export const categoryImplication = (c?: string | null) => implication(CATEGORY_IMPLIES, c)

const PURPOSE_IMPLIES: Record<string, string> = {
  BASELINE_MEASUREMENT: 'Establishes the starting point. Its value comes later, when something '
    + 'is compared to it.',
  TREND_TRACKING: 'Only means anything as part of a series. The instrument must stay identical '
    + 'between waves — a reworded question makes the change unreadable, and the trend screen '
    + 'will withhold the delta and say so.',
  ISSUE_DIAGNOSTICS: 'Diagnoses why, not how many. Open-text answers are likely, so plan for '
    + 'the coding frame in Verbatims.',
  OPPOSITION_INTEL: 'Measures the other side’s position. Nothing changes technically; it is '
    + 'a label for who reads the results and how carefully they are held.',
  CANDIDATE_VIABILITY: 'Tests whether somebody can win. Link the candidates so their weighted '
    + 'share is pushed onto their record when the weight run finishes.',
  GET_OUT_THE_VOTE: 'Mobilisation, not measurement. Usually paired with an assignment plan '
    + 'rather than with a sampling design.',
  MESSAGE_TESTING: 'Compares framings. Randomise option order, and expect to read this through '
    + 'Crosstabs rather than through headline estimates.',
  TURNOUT_MODELLING: 'Feeds a model rather than a headline. Screener questions matter more '
    + 'than the ballot question here.',
}
export const purposeImplication = (c?: string | null) => implication(PURPOSE_IMPLIES, c)

const AUDIENCE_IMPLIES: Record<string, string> = {
  ALL_VOTERS: 'No screening. Anybody in scope may be interviewed.',
  REGISTERED_VOTERS: 'Only people on the register. Someone has to be able to check that in the '
    + 'field — plan a screener question for it.',
  UNDECIDED_VOTERS: 'A screened subgroup, so the achieved sample will be a fraction of the '
    + 'contacts made. Raise the target accordingly.',
  OPPOSITION_SUPPORTERS: 'A screened subgroup. Expect a high refusal rate and budget contact '
    + 'attempts for it.',
  PARTY_MEMBERS: 'A membership list, not the public. The sampling design should say where the '
    + 'list came from.',
  YOUTH_VOTERS: 'Age-screened. Declare the age band in the quota dimensions or the sample will '
    + 'drift older, which is the way it always drifts.',
  WOMEN_VOTERS: 'Gender-screened. Declare it as a quota rather than leaving it to the field.',
  RURAL_VOTERS: 'Screened on where they live. The scope unit does most of this work already; '
    + 'the label is what tells a reader which half of a mixed county was meant.',
  URBAN_VOTERS: 'Screened on where they live. As above — the scope does the work, the label '
    + 'records the intent.',
}
export const audienceImplication = (c?: string | null) => implication(AUDIENCE_IMPLIES, c)

const METHODOLOGY_IMPLIES: Record<string, string> = {
  F2F: 'Interviewers in the field. GPS and travel checks apply, and the fieldwork plan needs '
    + 'assignments before the poll can go live.',
  CATI: 'Telephone. No GPS, so the location integrity rules will not fire — quality rests on '
    + 'duration, back-checks and near-duplicate detection instead.',
  ONLINE: 'Self-completed. Nobody is present to probe, so open-text answers will be shorter '
    + 'and straightlining is likelier.',
  USSD: 'Very short sessions on any handset. Long option lists do not fit; keep questions to a '
    + 'handful of choices.',
  SMS: 'One question at a time, with drop-off between them. Expect partials and put the '
    + 'question that matters first.',
  WHATSAPP: 'Conversational and asynchronous. An interview may span hours, so the duration '
    + 'checks read differently here.',
  HYBRID: 'More than one mode in one poll. Mode is a real source of difference — keep it as a '
    + 'crosstab dimension in mind when the results come back.',
}
export const methodologyImplication = (c?: string | null) => implication(METHODOLOGY_IMPLIES, c)

const FREQUENCY_IMPLIES: Record<string, string> = {
  ONE_TIME: 'Runs once. It has no next wave, and the schedule step has nothing to set.',
  DAILY: 'A wave every day. Only workable with a short instrument and a standing field team.',
  WEEKLY: 'A wave every week.',
  FORTNIGHTLY: 'A wave every two weeks.',
  MONTHLY: 'A wave every month, on the same day of the month.',
  QUARTERLY: 'A wave every three months.',
  EVENT_DRIVEN: 'Runs when something happens. There is no interval to schedule, so each wave '
    + 'is opened by hand.',
  CONTINUOUS: 'Collects without stopping, so it has no waves at all.',
}
export const frequencyImplication = (c?: string | null) => implication(FREQUENCY_IMPLIES, c)

const LINK_TYPE_LABEL: Record<string, string> = {
  CANDIDATE: 'Candidate', PARTY: 'Party', COALITION: 'Coalition', PROGRAM: 'Programme',
  UNIT: 'Geographic unit', EVENT: 'Coverage event',
}
export const linkTypeLabel = (c?: string | null) => label(LINK_TYPE_LABEL, c)

const LINK_TYPE_IMPLIES: Record<string, string> = {
  CANDIDATE: 'Their record shows this poll, and the weighted share lands on it when a run '
    + 'finishes. The list is narrowed to candidates whose seat can be contested in this '
    + 'poll’s scope.',
  PARTY: 'Filters and dossiers can find this poll from the party.',
  COALITION: 'As for a party, one level up.',
  PROGRAM: 'Lets the results be read against the programme’s beneficiaries.',
  UNIT: 'A place this poll is about beyond its own scope — a neighbouring ward, a station '
    + 'under review.',
  EVENT: 'A rally or broadcast this poll was fielded around.',
}
export const linkTypeImplication = (c?: string | null) => implication(LINK_TYPE_IMPLIES, c)

const AUDIO_MODE_LABEL: Record<string, string> = {
  OFF: 'No recording', OPTIONAL: 'Optional', REQUIRED: 'Required',
}
export const audioModeLabel = (c?: string | null) => label(AUDIO_MODE_LABEL, c)

const AUDIO_MODE_IMPLIES: Record<string, string> = {
  OFF: 'Nothing is recorded. The consent script needs no audio disclosure, and the capture '
    + 'screen offers no microphone.',
  OPTIONAL: 'Recording starts when the interviewer opens an interview and stops when they '
    + 'submit it — but an interview with no audio is still accepted. The consent script must '
    + 'disclose the recording in every language it exists in; the server refuses to launch the '
    + 'poll otherwise.',
  REQUIRED: 'As Optional, and an interview with no audio is flagged. Choose this only where '
    + 'every interviewer has a working microphone — a flag nobody can clear is a queue nobody '
    + 'reads.',
}
export const audioModeImplication = (c?: string | null) => implication(AUDIO_MODE_IMPLIES, c)
